/* eslint-disable @typescript-eslint/no-explicit-any */
import { ICellEditorParams, ICellRendererParams, ITooltipParams, ValueFormatterParams } from 'ag-grid-community';
import { default as moment } from 'moment';
import { AddressRef, UserRef } from '../schema/schema.interface';
import { AsyncCellComponent, ColumnDefinition, MissionResultRendererComponent } from '@res/ngg-core';

const machineTerminologyKeyValueList = [
  { key: 'KIOSK', value: 'KIOSK' },
  { key: 'KIOSK+CASHLINKAPP', value: 'KIOSK+CASHLINKAPP' },
  { key: 'TABLET', value: 'TABLET' },
  { key: 'TABLET+CASHLINKAPP', value: 'TABLET+CASHLINKAPP' },
  { key: 'CASH', value: 'CASH' },
];

const machineTerminologyList = ['KIOSK', 'KIOSK+CASHLINKAPP', 'TABLET', 'TABLET+CASHLINKAPP', 'CASH'];

export const machineBaseSchema: ColumnDefinition[] = [
  // { id: 'id', name: 'id' },
  {
    id: 'cId',
    name: 'cId',
    truncate: 12,
    isReadOnly: true,
    colDef: { cellRenderer: 'agGroupCellRenderer' },
  },
  {
    id: 'appId',
    name: 'appId',
    truncate: 5,
    isReadOnly: true,
    // colDef: { cellRenderer: 'agGroupCellRenderer' },
  },
  {
    id: 'type',
    name: 'deviceType',
    type: 'convertibleDropdown',
    colDef: { editable: true, cellEditor: 'agTextCellEditor' },
    dropdownValues: { dropdownCollectionField: 'type', addEmpty: true },
    isReadOnly: false, //just because we have wrong devices(empty)
  },
  {
    id: 'deviceType',
    name: 'deviceNature',
    type: 'dropdown',
    colDef: {
      editable: true,
      cellEditor: 'agSelectCellEditor',
      cellEditorParams: { values: machineTerminologyList },
    },
    dropdownValues: machineTerminologyKeyValueList,
    isReadOnly: false, //just because we have wrong devices(empty)
  },
  {
    id: 'status',
    name: 'status',
    type: 'dropdown',
    colDef: {
      editable: true,
      cellEditor: 'agSelectCellEditor',
      cellEditorParams: { values: ['active', 'inactive'] },
    },
    dropdownValues: [
      { key: 'active', value: 'active' },
      { key: 'inactive', value: 'inactive' },
    ],
  },
  {
    id: 'lastService',
    name: 'lastService',
    type: 'datepicker',
    colDef: { editable: true, cellEditor: 'agDateCellEditor', cellDataType: 'date' },
    disableMapper: {
      field: 'deviceType',
      value: 'CASHLINKAPP',
      targetFields: ['nextService', 'lastService', 'endOfGuarantee'],
    },
  },
  {
    id: 'nextService',
    name: 'nextService',
    colDef: { editable: true, cellEditor: 'agDateCellEditor', cellDataType: 'date' },
    type: 'datepicker',
  },
  {
    id: 'endOfGuarantee',
    name: 'endOfGuarantee',
    type: 'datepicker',
    colDef: {
      editable: true,
      cellEditor: 'agDateCellEditor',
      cellDataType: 'date',
      width: 140,
      minWidth: 140,
      maxWidth: 140,
      headerClass: 'text-center',
      cellStyle: {
        textAlign: 'center',
      },
      cellRenderer: MissionResultRendererComponent,
      tooltipValueGetter: (p: ITooltipParams) => moment(p.value).format('YYYY-MM-DD').toString(),
    },
    isReadOnly: true,
  },
  {
    id: 'creationDate',
    name: 'creationDate',
    type: 'datepicker',
    isReadOnly: true,
    colDef: { cellDataType: 'date' },
    default: new Date(),
  },
];
export const machineAdminSchema: ColumnDefinition[] = [
  // { id: 'id', name: 'id' },
  ...machineBaseSchema,
  {
    id: 'userId',
    name: 'userId',
    type: 'dropdown',
    dropdownValues: { dropdownCollectionField: 'users/companyName', addEmpty: true },
    colDef: {
      editable: true,
      cellEditor: 'agRichSelectCellEditor',
      cellEditorParams: {
        values: (params: ICellEditorParams) => [
          ...((params.context ?? params.data.context).userStore as any).users().map((x: { ref: UserRef }) => x.ref),
          null,
        ],
      },
      valueFormatter: (params: ValueFormatterParams) =>
        ((params.context ?? params.data.context).userStore as any)
          .users()
          .find((x: { ref: UserRef }) => x.ref.id === params.value?.id)?.companyName ?? params.value?.companyName,
      cellRendererParams: {
        promise: (params: ICellRendererParams) =>
          Promise.resolve(
            ((params.context ?? params.data.context).userStore as any)
              .users()
              .find(
                (user: { ref: UserRef }) =>
                  params?.value !== undefined &&
                  ((typeof params?.value === 'string' && user.ref.id === params?.value?.split('/')?.[1]) ||
                    user.ref.id === params?.value?.id),
              )?.companyName || null,
          ),
      },
      cellRenderer: AsyncCellComponent,
    },
  },
  {
    id: 'crmSignKey',
    name: 'crmSignKey',
    type: 'textbox',
    truncate: 15,
    isReadOnly: true,
    isCanCopy: true,
  },
  {
    id: 'addressId',
    colDef: {
      editable: true,
      cellEditor: 'agRichSelectCellEditor',
      cellEditorParams: {
        values: (params: ICellEditorParams) => [
          ...((params.context ?? params.data.context).addressStore as any)
            .addresses()
            .filter(
              (address: { ref: AddressRef }) =>
                address.ref.collection.path === `users/${params.data.userId?.id}/addresses`,
            )
            .map((address: { ref: AddressRef }) => address.ref),
          null,
        ],
      },
      valueFormatter: (params: ValueFormatterParams) =>
        ((params.context ?? params.data.context).addressStore as any)
          .addresses()
          .find((x: { ref: AddressRef }) => x.ref.id === params.value?.id)?.addressName ?? params.value?.addressName,
      cellRendererParams: {
        promise: (params: ICellRendererParams) =>
          Promise.resolve(
            ((params.context ?? params.data.context).addressStore as any)
              .addresses()
              .find(
                (address: { ref: AddressRef }) =>
                  params?.value !== undefined &&
                  ((typeof params?.value === 'string' && address.ref.id === params?.value?.split('/')?.[3]) ||
                    address.ref.id === params?.value?.id),
              )?.addressName || null,
          ),
      },
      cellRenderer: AsyncCellComponent,
    },
    name: 'addressId',
    type: 'dropdownGroup',
    dropdownValues: {
      dropdownCollectionField: 'addresses/addressName',
      parentCollectionField: 'users/companyName',
      addEmpty: true,
      isReference: true,
    },
  },
];
export const machineForCustomerSchema: ColumnDefinition[] = [
  // { id: 'id', name: 'id' },
  {
    id: 'cId',
    name: 'cId',
    truncate: 15,
    isReadOnly: true,
    colDef: {
      cellStyle: { fontWeight: 'bold' },
      cellRenderer: 'agGroupCellRenderer',
    },
  },
  {
    id: 'type',
    name: 'deviceType',
    type: 'convertibleDropdown',
    colDef: {
      width: 180,
      minWidth: 180,
      maxWidth: 180,
      editable: true,
      cellEditor: 'agTextCellEditor',
    },
    dropdownValues: { dropdownCollectionField: 'type', addEmpty: true },
    isReadOnly: false, //just because we have wrong devices(empty)
  },
  {
    id: 'deviceType',
    name: 'deviceNature',
    type: 'dropdown',
    colDef: {
      width: 180,
      minWidth: 180,
      maxWidth: 180,
      editable: true,
      cellEditor: 'agSelectCellEditor',
      cellEditorParams: { values: machineTerminologyList },
    },
    dropdownValues: machineTerminologyKeyValueList,
    isReadOnly: false, //just because we have wrong devices(empty)
  },
  {
    id: 'status',
    name: 'status',
    type: 'dropdown',
    colDef: {
      editable: true,
      width: 150,
      minWidth: 150,
      maxWidth: 150,
      cellEditor: 'agSelectCellEditor',
      cellEditorParams: {
        values: ['active', 'inactive'],
      },
    },
    dropdownValues: [
      { key: 'active', value: 'active' },
      { key: 'inactive', value: 'inactive' },
    ],
  },
  {
    id: 'endOfGuarantee',
    name: 'endOfGuarantee',
    type: 'datepicker',
    colDef: {
      editable: true,
      cellEditor: 'agDateCellEditor',
      cellDataType: 'date',
      width: 140,
      minWidth: 140,
      maxWidth: 140,
      headerClass: 'text-center',
      cellStyle: {
        textAlign: 'center',
      },
      cellRenderer: MissionResultRendererComponent,
      tooltipValueGetter: (p: ITooltipParams) => moment(p.value).format('YYYY-MM-DD').toString(),

      // valueFormatter: (params) => (params.value >= new Date() ? 'true' : 'false'),
    },
    isReadOnly: true,
  },
  {
    id: 'creationDate',
    name: 'creationDate',
    type: 'datepicker',

    isReadOnly: true,
    colDef: { cellDataType: 'date', width: 200, minWidth: 200, maxWidth: 200 },
    default: new Date(),
  },

  {
    id: 'machineUrl',
    name: 'machineUrl',
    colDef: {
      editable: true,
      cellDataType: 'string',
    },
  },
];
